<script setup>
import { onUnmounted } from "vue"
import { useAdminFilters } from "../composables/useAdminFilters"

const props = defineProps({
  url: { type: String, required: true },
  clearUrl: { type: String, required: true },
  savedSearchesUrl: { type: String, required: true },
  csrfToken: { type: String, required: true },
  initialFilters: { type: Object, default: () => ({}) },
  advancedExpanded: { type: Boolean, default: false },
  contentTypes: { type: Array, default: () => [] },
  books: { type: Array, default: () => [] },
  statusOptions: { type: Array, default: () => [] },
  savedSearches: { type: Array, default: () => [] },
  labels: { type: Object, default: () => ({}) },
})

const label = (key, fallback) => props.labels[key] || fallback

const {
  filters,
  advancedOpen,
  saveName,
  showClear,
  showSaveSearch,
  advancedToggleLabel,
  debouncedSearch,
  onSelectChange,
  clearFilters,
  toggleAdvanced,
  saveSearch,
  cleanup,
} = useAdminFilters({
  url: props.url,
  initialFilters: props.initialFilters,
  advancedExpanded: props.advancedExpanded,
  savedSearchesUrl: props.savedSearchesUrl,
  csrfToken: props.csrfToken,
  labels: props.labels,
})

onUnmounted(() => {
  cleanup()
})
</script>

<template>
  <div class="admin-contents-filters">
    <form @submit.prevent="onSelectChange">
      <div class="filter-group">
        <span class="filter-label">{{ label("search", "Search") }}:</span>
        <input
          v-model="filters.q"
          type="text"
          class="filter-input"
          :placeholder="label('searchPlaceholder', 'Search contents...')"
          @input="debouncedSearch"
        >
      </div>

      <div class="filter-group">
        <span class="filter-label">{{ label("type", "Type") }}:</span>
        <select v-model="filters.type" class="filter-select" @change="onSelectChange">
          <option value="">
            {{ label("allTypes", "All Types") }}
          </option>
          <option v-for="option in contentTypes" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <span class="filter-label">{{ label("status", "Status") }}:</span>
        <select v-model="filters.status" class="filter-select" @change="onSelectChange">
          <option value="">
            {{ label("allStatuses", "All Statuses") }}
          </option>
          <option v-for="option in statusOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <span class="filter-label">{{ label("language", "Language") }}:</span>
        <select v-model="filters.language" class="filter-select" @change="onSelectChange">
          <option value="">
            {{ label("allLanguages", "All Languages") }}
          </option>
          <option value="de">
            {{ label("german", "German (DE)") }}
          </option>
          <option value="en">
            {{ label("english", "English (EN)") }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <span class="filter-label">{{ label("book", "Book") }}:</span>
        <select v-model="filters.book_id" class="filter-select" @change="onSelectChange">
          <option value="">
            {{ label("allBooks", "All Books") }}
          </option>
          <option v-for="book in books" :key="book.value" :value="book.value">
            {{ book.label }}
          </option>
        </select>
      </div>

      <div class="filter-group">
        <button type="submit" class="btn btn-primary btn-sm">
          {{ label("filter", "Filter") }}
        </button>
        <a
          v-if="showClear"
          :href="clearUrl"
          class="btn btn-secondary btn-sm"
          @click="clearFilters"
        >{{ label("clear", "Clear") }}</a>
        <button type="button" class="btn btn-sm btn-secondary" @click="toggleAdvanced">
          {{ advancedToggleLabel }}
        </button>
      </div>

      <div
        class="advanced-filters"
        :class="{ expanded: advancedOpen }"
      >
        <div class="advanced-filter-row">
          <div class="filter-group">
            <span class="filter-label">AP Value:</span>
            <div class="range-inputs">
              <input v-model="filters.ap_min" type="number" class="filter-input small" min="0" placeholder="Min" @change="onSelectChange">
              <span>–</span>
              <input v-model="filters.ap_max" type="number" class="filter-input small" min="0" placeholder="Max" @change="onSelectChange">
            </div>
          </div>

          <div class="filter-group">
            <span class="filter-label">AsP Cost:</span>
            <div class="range-inputs">
              <input v-model="filters.asp_min" type="number" class="filter-input small" min="0" placeholder="Min" @change="onSelectChange">
              <span>–</span>
              <input v-model="filters.asp_max" type="number" class="filter-input small" min="0" placeholder="Max" @change="onSelectChange">
            </div>
          </div>

          <div class="filter-group">
            <span class="filter-label">KaP Cost:</span>
            <div class="range-inputs">
              <input v-model="filters.kap_min" type="number" class="filter-input small" min="0" placeholder="Min" @change="onSelectChange">
              <span>–</span>
              <input v-model="filters.kap_max" type="number" class="filter-input small" min="0" placeholder="Max" @change="onSelectChange">
            </div>
          </div>
        </div>

        <div class="advanced-filter-row">
          <div class="filter-group">
            <span class="filter-label">Feature:</span>
            <input v-model="filters.feature" type="text" class="filter-input" placeholder="e.g., Antimagie" @change="onSelectChange">
          </div>

          <div class="filter-group">
            <span class="filter-label">Distribution:</span>
            <input v-model="filters.distribution" type="text" class="filter-input" placeholder="e.g., Allgemein" @change="onSelectChange">
          </div>

          <div class="filter-group">
            <span class="filter-label">Category:</span>
            <input v-model="filters.category" type="text" class="filter-input" placeholder="e.g., Kampf" @change="onSelectChange">
          </div>

          <div class="filter-group">
            <span class="filter-label">Has Image:</span>
            <select v-model="filters.has_image" class="filter-select" @change="onSelectChange">
              <option value="">
                Any
              </option>
              <option value="1">
                Yes
              </option>
              <option value="0">
                No
              </option>
            </select>
          </div>
        </div>
      </div>
    </form>

    <div v-if="savedSearches.length" class="saved-searches">
      <span class="saved-label">{{ label("saved", "Saved") }}:</span>
      <a
        v-for="search in savedSearches"
        :key="search.url"
        :href="search.url"
        class="saved-search-link"
      >{{ search.name }}</a>
    </div>

    <div v-if="showSaveSearch" class="save-search-form">
      <form class="inline-form" @submit="saveSearch">
        <input
          v-model="saveName"
          type="text"
          class="filter-input small"
          :placeholder="label('saveAs', 'Save as...')"
          required
        >
        <button type="submit" class="btn btn-sm btn-secondary">
          💾 {{ label("save", "Save") }}
        </button>
      </form>
    </div>
  </div>
</template>
